/* eslint-disable @typescript-eslint/explicit-function-return-type */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* @typescript-eslint/ban-ts-ignore */

import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit';
import { AppThunk, RootState } from 'store';
import { currentUserSelector } from './userSlice';
import createUserApi from 'api/user';
import { appContextSelector, loadEquivalents } from 'modules/app/appSlice';
import { loadProjects } from 'modules/projects/projectsSlice';
import { getOrderHistory } from 'modules/orders/ordersSlice';
import { loadTransactions } from 'modules/transactions/transactionsSlice';
import { employeeBenefitSubscription, incompleteEmployeeBenefitSubscription } from 'modules/subscriptions/subscriptionSlice';
import { differenceInMonths } from 'date-fns';
interface State extends Attribution {
  loading: boolean;
  mychoooseCustomerCo2?: CO2Attribution;
  wechoooseUserCo2?: CO2Attribution;
}

const initialState: State = {
  loading: false,
  co2: undefined,
  statistics: undefined,
  mychoooseCustomerCo2: undefined,
  wechoooseUserCo2: undefined,
};

const slice = createSlice({
  name: 'attribution',
  initialState,
  reducers: {
    setAttribution(state: State, action: PayloadAction<Attribution | undefined>): State {
      state.co2 = action.payload?.co2;
      state.statistics = action.payload?.statistics;
      return state;
    },
    setMychoooseCustomerCo2(state: State, action: PayloadAction<CO2Attribution | undefined>): State {
      state.mychoooseCustomerCo2 = action.payload;
      return state;
    },
    setWechoooseUserCo2(state: State, action: PayloadAction<CO2Attribution | undefined>): State {
      state.wechoooseUserCo2 = action.payload;
      return state;
    },
    setLoading(state: State, action: PayloadAction<boolean>): State {
      state.loading = action.payload;
      return state;
    },
  },
});

const { setAttribution, setLoading, setMychoooseCustomerCo2, setWechoooseUserCo2 } = slice.actions;

export { setAttribution, setMychoooseCustomerCo2 };

export default slice.reducer;

function getCarbonBasedOnContext(attribution: Attribution, context: PortalContext): number {
  let carbon = 0;

  if (context === 'connect') {
    carbon = attribution?.co2?.kilosCo2Facilitated ?? 0;
  } else {
    carbon = attribution?.co2?.kilosCo2Owned ?? 0;
  }
  return carbon;
}

export const syncAttribution = (cb?: CallableFunction): AppThunk => async (dispatch, getState): Promise<void> => {
  const { accounts } = getState().user;
  const currentAccount = currentUserSelector(getState());
  const mychoooseAccount = accounts?.find(acc => acc.type === 'MyChooose');

  const context = appContextSelector(getState());
  const userApi = createUserApi(context);
  dispatch(setLoading(true));
  try {
    let attribution;
    let customerAttribution;
    let userAttribution;
    switch (context) {
      case 'connect':
        attribution = await userApi.getAttributionForPartnership(currentAccount?.partnershipId ?? '');

        break;
      case 'wechooose':
        attribution = await userApi.getAttributionForCustomer(currentAccount?.customerId ?? '');
        if (mychoooseAccount) {
          customerAttribution = await userApi.getAttributionForCustomer(mychoooseAccount?.customerId ?? '');
        }
        userAttribution = await userApi.getAttributionForUser();
        break;
      case 'mychooose':
        attribution = await userApi.getAttributionForUser();
        customerAttribution = await userApi.getAttributionForCustomer(currentAccount?.customerId ?? '');
        break;
      default:
        attribution = await userApi.getAttributionForUser();
        break;
    }

    const carbon = getCarbonBasedOnContext(attribution, context);

    dispatch(setAttribution(attribution));
    if (customerAttribution) {
      dispatch(setMychoooseCustomerCo2(customerAttribution.co2));
    }

    if (userAttribution) {
      dispatch(setWechoooseUserCo2(userAttribution.co2));
    }
    dispatch(loadEquivalents(carbon));
    dispatch(loadProjects(undefined, true));
    if (context === 'connect') {
      dispatch(loadTransactions());
    } else {
      dispatch(getOrderHistory());
    }

    dispatch(setLoading(false));
  } catch {
    dispatch(setLoading(false));
  } finally {
    cb && cb();
  }
};
export const loadAttributionForCustomer = (customerId = 'default'): AppThunk => async (dispatch, getState): Promise<void> => {
  const context = appContextSelector(getState());
  const userApi = createUserApi(context);
  try {
    const attribution = await userApi.getAttributionForCustomer(customerId);
    const carbon = getCarbonBasedOnContext(attribution, context);
    dispatch(setAttribution(attribution));
    dispatch(loadEquivalents(carbon));
  } catch {
    //err
  }
};

export const loadAttributionForPartnership = (partnershipId = 'default'): AppThunk => async (dispatch, getState): Promise<void> => {
  const context = appContextSelector(getState());
  const userApi = createUserApi(context);

  try {
    const attribution = await userApi.getAttributionForPartnership(partnershipId);
    const carbon = getCarbonBasedOnContext(attribution, context);

    dispatch(setAttribution(attribution));
    dispatch(loadEquivalents(carbon));
  } catch {
    //err
  }
};
export const loadAttributionForUser = (): AppThunk => async (dispatch, getState): Promise<void> => {
  const context = appContextSelector(getState());
  const userApi = createUserApi(context);

  try {
    const attribution = await userApi.getAttributionForUser();
    const carbon = getCarbonBasedOnContext(attribution, context);

    dispatch(setAttribution(attribution));
    dispatch(loadEquivalents(carbon));
  } catch {
    //err
  }
};

export const loadCustomerAttributionDataForMyChooose = (customerId = 'default'): AppThunk => async (dispatch, getState): Promise<void> => {
  const context = appContextSelector(getState());
  const userApi = createUserApi(context);
  try {
    const attribution = await userApi.getAttributionForCustomer(customerId);
    dispatch(setMychoooseCustomerCo2(attribution.co2));
  } catch {
    //err
  }
};

export const loadUserAttributionDataForWeChooose = (): AppThunk => async (dispatch, getState): Promise<void> => {
  const context = appContextSelector(getState());
  const userApi = createUserApi(context);
  try {
    const attribution = await userApi.getAttributionForUser();
    dispatch(setWechoooseUserCo2(attribution.co2));
  } catch {
    //err
  }
};

export const co2ByOrderTypeForMonth = createSelector(
  (state: RootState) => {
    const attribution = state.attribution;
    if (attribution && attribution.statistics) {
      return attribution.statistics[!state.app.context ? 'paidFor' : 'owned']?.thisMonth?.co2PerCategory;
    }
    return [];
  },
  categories => {
    const mappedCategories = categories
      ?.map(category =>
        !category.category ? { name: 'Other', value: category.kilosCo2 } : { name: category.category, value: category.kilosCo2 / 1000 },
      )
      .sort((itemA, itemB) => itemB.value - itemA.value);
    return mappedCategories;
  },
);

export const co2ByOrderTypeForLastMonth = createSelector(
  (state: RootState) => {
    const attribution = state.attribution;
    if (attribution && attribution.statistics) {
      return attribution.statistics[!state.app.context ? 'paidFor' : 'owned']?.lastMonth?.co2PerCategory;
    }
    return [];
  },
  categories => {
    const mappedCategories = categories
      ?.map(category =>
        !category.category ? { name: 'Other', value: category.kilosCo2 } : { name: category.category, value: category.kilosCo2 / 1000 },
      )
      .sort((itemA, itemB) => itemB.value - itemA.value);
    return mappedCategories;
  },
);

export const co2ByOrderTypeForYear = createSelector(
  (state: RootState) => {
    const attribution = state.attribution;
    if (attribution && attribution.statistics) {
      return attribution.statistics[!state.app.context ? 'paidFor' : 'owned']?.thisYear?.co2PerCategory;
    }
    return [];
  },
  categories => {
    const mappedCategories = categories
      ?.map(category =>
        !category.category ? { name: 'Other', value: category.kilosCo2 } : { name: category.category, value: category.kilosCo2 / 1000 },
      )
      .sort((itemA, itemB) => itemB.value - itemA.value);
    return mappedCategories;
  },
);

export const co2ByOrderTypeForLastYear = createSelector(
  (state: RootState) => {
    const attribution = state.attribution;
    if (attribution && attribution.statistics) {
      return attribution.statistics[!state.app.context ? 'paidFor' : 'owned']?.lastYear?.co2PerCategory;
    }
    return [];
  },
  categories => {
    const mappedCategories = categories
      ?.map(category =>
        !category.category ? { name: 'Other', value: category.kilosCo2 } : { name: category.category, value: category.kilosCo2 / 1000 },
      )
      .sort((itemA, itemB) => itemB.value - itemA.value);
    return mappedCategories;
  },
);

export const co2ByOrderTypeForThisWeek = createSelector(
  (state: RootState) => {
    const attribution = state.attribution;
    if (attribution && attribution.statistics) {
      return attribution.statistics[!state.app.context ? 'paidFor' : 'owned']?.thisWeek?.co2PerCategory;
    }
    return [];
  },
  categories => {
    const mappedCategories = categories
      ?.map(category =>
        !category.category ? { name: 'Other', value: category.kilosCo2 } : { name: category.category, value: category.kilosCo2 / 1000 },
      )
      .sort((itemA, itemB) => itemB.value - itemA.value);
    return mappedCategories;
  },
);

export const co2ByOrderTypeForLastWeek = createSelector(
  (state: RootState) => {
    const attribution = state.attribution;
    if (attribution && attribution.statistics) {
      return attribution.statistics[!state.app.context ? 'paidFor' : 'owned']?.lastWeek?.co2PerCategory;
    }
    return [];
  },
  categories => {
    const mappedCategories = categories
      ?.map(category =>
        !category.category ? { name: 'Other', value: category.kilosCo2 } : { name: category.category, value: category.kilosCo2 / 1000 },
      )
      .sort((itemA, itemB) => itemB.value - itemA.value);
    return mappedCategories;
  },
);

export const numberOfOrdersForMonth = createSelector(
  (state: RootState) => {
    const attribution = state.attribution;
    if (attribution && attribution.statistics) {
      return attribution.statistics[!state.app.context ? 'paidFor' : 'owned']?.thisMonth?.countPerCategory;
    }
    return [];
  },
  categories => {
    const mappedCategories = categories
      ?.map(category => (!category.category ? { name: 'Other', value: category.count } : { name: category.category, value: category.count }))
      .sort((itemA, itemB) => itemB.value - itemA.value);
    return mappedCategories;
  },
);

export const numberOfOrdersForLastMonth = createSelector(
  (state: RootState) => {
    const attribution = state.attribution;
    if (attribution && attribution.statistics) {
      return attribution.statistics[!state.app.context ? 'paidFor' : 'owned']?.lastMonth?.countPerCategory;
    }
    return [];
  },
  categories => {
    const mappedCategories = categories
      ?.map(category => (!category.category ? { name: 'Other', value: category.count } : { name: category.category, value: category.count }))
      .sort((itemA, itemB) => itemB.value - itemA.value);
    return mappedCategories;
  },
);

export const numberOfOrdersForYear = createSelector(
  (state: RootState) => {
    const attribution = state.attribution;
    if (attribution && attribution.statistics) {
      return attribution.statistics[!state.app.context ? 'paidFor' : 'owned']?.thisYear?.countPerCategory;
    }
    return [];
  },
  categories => {
    const mappedCategories = categories
      ?.map(category => (!category.category ? { name: 'Other', value: category.count } : { name: category.category, value: category.count }))
      .sort((itemA, itemB) => itemB.value - itemA.value);
    return mappedCategories;
  },
);

export const numberOfOrdersForLastYear = createSelector(
  (state: RootState) => {
    const attribution = state.attribution;
    if (attribution && attribution.statistics) {
      return attribution.statistics[!state.app.context ? 'paidFor' : 'owned']?.lastYear?.countPerCategory;
    }
    return [];
  },
  categories => {
    const mappedCategories = categories
      ?.map(category => (!category.category ? { name: 'Other', value: category.count } : { name: category.category, value: category.count }))
      .sort((itemA, itemB) => itemB.value - itemA.value);
    return mappedCategories;
  },
);

export const numberOfOrdersForThisWeek = createSelector(
  (state: RootState) => {
    const attribution = state.attribution;
    if (attribution && attribution.statistics) {
      return attribution.statistics[!state.app.context ? 'paidFor' : 'owned']?.thisWeek?.countPerCategory;
    }
    return [];
  },
  categories => {
    const mappedCategories = categories
      ?.map(category => (!category.category ? { name: 'Other', value: category.count } : { name: category.category, value: category.count }))
      .sort((itemA, itemB) => itemB.value - itemA.value);
    return mappedCategories;
  },
);

export const numberOfOrdersForLastWeek = createSelector(
  (state: RootState) => {
    const attribution = state.attribution;
    if (attribution && attribution.statistics) {
      return attribution.statistics[!state.app.context ? 'paidFor' : 'owned']?.lastWeek?.countPerCategory;
    }
    return [];
  },
  categories => {
    const mappedCategories = categories
      ?.map(category => (!category.category ? { name: 'Other', value: category.count } : { name: category.category, value: category.count }))
      .sort((itemA, itemB) => itemB.value - itemA.value);
    return mappedCategories;
  },
);

export const co2AmountSelector = createSelector(
  (state: RootState) => appContextSelector(state),
  (state: RootState) => state.attribution,
  (context, attribution) => getCarbonBasedOnContext(attribution, context),
);

export const co2AmountSelectorPersonal = createSelector(
  (state: RootState) => state.attribution.co2,
  co2 => co2?.kilosCo2PaidFor ?? 0,
);

export const co2AmountSelectorUserWechooose = createSelector(
  (state: RootState) => state.attribution.wechoooseUserCo2,
  co2 => co2?.kilosCo2Owned ?? 0,
);

export const co2AmountSelectorMyChooose = createSelector(
  (state: RootState) => state.attribution.co2,
  co2 => co2?.kilosCo2Owned ?? 0,
);

export const monthsSubscribedSelector = createSelector(
  (state: RootState) => employeeBenefitSubscription(state),
  (state: RootState) => incompleteEmployeeBenefitSubscription(state),
  (activeSubscription, nonRecurringSubscription) => {
    const subscription = nonRecurringSubscription ? nonRecurringSubscription : activeSubscription;
    if (subscription) {
      const diff = differenceInMonths(Date.now(), new Date(subscription.startDate ?? ''));
      return diff || 0;
    }

    return null;
  },
);
